<template lang="pug">
.appointment-detail.flex
  .data.flex.flex-1.column.gap-16
    .col
      .title 個案姓名
      .data {{ data.user?.family_name }}{{data.user?.given_name}}
    .col
      .title 標記
      .data.flex.align-center.gap-6
        el-tag(v-for="n in data.tags", :key="n", type="info", size="small") {{ n }}
        el-select.selector-no-text.small(
          v-if="data.canEdit"
          multiple, v-model="data.tags", @change="updateBranchTags(data)")
          el-option(
            v-for="tag in tags",
            v-if="tag.public",
            :key="tag.name", :label="tag.name", :value="tag.name")
    .col
      .title 專案
      .data {{ projectName }}
    .col
      .title 專業人士
      .data
        pro-badge(:professional="data.professional")
    .col
      .title 本次晤談為
      .data.flex.align-center.gap-6
        .txt {{ $msg(`appointment.type.${detail.appointment_type}`) }}
        template(v-if="detail.appointment_type !== 'individual' && (detail.partners || []).length > 0")
          span (
          .partners.flex.align-center
            .partner.flex.align-center(
              @click="showPartner(p)"
              v-for="(p, idx) in data.partners")
              template
                span(v-if="idx !== 0") 、
                span.link-text.clickable {{ p.family_name }}{{ p.given_name }}
          span )
    .col
      .title 型態
      .data {{ $msg(`appointment.locationType.${detail.location_type}`)}}
    .col
      .title 預約時間與時長
      .data.flex.align-center.gap-6
        span {{ detail.startTime }}
        span ({{detail.length}} hr)
    .col
      .title 預約空間
      .data {{ detail.room?.branch?.name }} {{ detail.room?.name }}
    .col
      .title 預約價位
      .data NT $ {{ detail.price }}
    .col
      .title 支付記錄
      .data.flex.align-center.gap-6(:class="{'danger-color': detail.payment === 'notpaid'}")
        span {{ $msg(`appointment.payment.${detail.payment}`) }}
        el-dropdown(
          v-if="paidCanChange(detail.payment) && (detail.canChangePaid || isUnlimitedAppointmentRole)"
          @command="updatePayment($event)", trigger="click")
          el-button.dropdown-button(icon="el-icon-arrow-down", size="mini")
          el-dropdown-menu(slot="dropdown")
            el-dropdown-item(command='notpaid') 尚未付款
            el-dropdown-item(command='cash') 現金支付
            el-dropdown-item(command='transfer') 轉帳支付
            el-dropdown-item(command='creditcard') 信用卡
    .col
      .title 個案記錄
      .data.flex.align-center.gap-10
        a.link-text(v-if="detail.record?.url", target="_blank", :href="detail.record.url") 下載紀錄
        span(v-else-if="detail.has_record") 已完成
        span(v-else) -
        //- 異動紀錄僅 admin 可見（後端只對 admin 回傳 record_log_count）
        .link-text.text-clickable(
          v-if="isAdmin && detail.record_log_count > 0"
          @click="showRecordLogs")
          | 異動紀錄 ({{ detail.record_log_count }})
          span.danger-color(v-if="detail.record_modified_count > 0") ・發佈後修改 {{ detail.record_modified_count }} 次
    .col
      .title 本次預約備註
      .data {{ detail.note || '-' }}
  .op
    el-dropdown(
      @command="execCommand($event)")
      el-button.dropdown-button(size="mini")
        .flex.align-center.gap-6
          span 預約操作
          i.el-icon-arrow-down
      el-dropdown-menu(slot="dropdown")
        el-dropdown-item(command="send") 傳送訊息給此個案
        el-dropdown-item(command="edit"
          :disabled="!detail.canEdit && !isUnlimitedAppointmentRole") 編輯預約
        el-dropdown-item(command="editNote") 編輯備註
        el-dropdown-item(
          v-if="!detail.receipt"
          :disabled="!detail.receipt"
          command="downloadReceipt") 下載此筆付款收據(未生成)
        el-dropdown-item(
          v-if="detail.receipt"
          command="downloadReceipt") 下載此筆付款收據
        el-dropdown-item(:disabled="detail.location_type === 'inhouse' || detail.location_type === 'phone'", command="copyLink") 複製諮商連結
        el-dropdown-item(
          command="recreateAppointment") 續約
        el-dropdown-item(command="cancel"
          :disabled="!detail.canCancel && !isUnlimitedAppointmentRole") 取消此預約記錄
  record-log-viewer(ref="recordLogViewer", append-to-body)
  person-viewer(
    hide-detail
    hide-open-chat
    append-to-body
    ref="personViewer"
  )
</template>

<script>
import { mapGetters } from 'vuex';
import {
  getUser,
} from '@/api/user';
import PersonViewer from '@/components/drawers/PersonViewer.vue';
import RecordLogViewer from '@/components/drawers/RecordLogViewer.vue';
import constant from '@/util/constant';

import {
  updateAppointmentTags,
  getAppointment,
  updatePayment,
  updateAppointmentNote,
} from '@/api/reserve';

export default {
  name: 'appointment-detail',
  components: {
    PersonViewer,
    RecordLogViewer,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    tags: {
      type: Array,
      default: () => {},
    },
    orgMap: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      detail: {},
    };
  },
  computed: {
    ...mapGetters(['isAdmin', 'isUnlimitedAppointmentRole']),
    dataID() {
      return this.data?.id;
    },
    projectName() {
      if (this.orgMap[this.detail?.project?.id]) {
        return `${this.orgMap[this.detail?.project?.id].name} - ${this.detail?.project?.name}`;
      }
      return this.detail?.project?.name || '--';
    },
  },
  watch: {
    dataID() {
      if (this.dataID) {
        this.loadData();
      }
    },
  },
  methods: {
    showRecordLogs() {
      const user = this.detail.user || {};
      this.$refs.recordLogViewer.$emit('show', {
        id: this.detail.id,
        subtitle: `${user.family_name || ''}${user.given_name || ''}｜${this.detail.startTime}`,
      });
    },
    showPartner(p) {
      this.$refs.personViewer.$emit('show', {
        type: 'id',
        data: p.id,
      });
    },
    paidCanChange(payment) {
      return ['notpaid', 'cash', 'transfer'].indexOf(payment) >= 0;
    },
    editAppointment() {
      this.$refs.appointmentEdit.$emit('show', this.data);
    },
    updatePayment(method) {
      this.$execWithLoading(async () => {
        await updatePayment(this.data.id, method);
        this.$emit('update:data', { ...this.data, payment: method });
        this.detail.payment = method;
        this.$showSuccess('更新成功');
      }, () => {
        this.$showError('更新失敗，請稍後再試');
      });
    },
    async updateBranchTags(branch) {
      return this.$execWithLoading(async () => {
        await updateAppointmentTags(branch.id, branch.tags);
        this.$showSuccess('已更新標記');
      }, () => {
        this.$showError('更新標記失敗');
      });
    },
    async loadData() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTs = parseInt(today.getTime() / 1000, 10);

      const now = new Date();
      const nowTs = parseInt(now.getTime() / 1000, 10);

      return this.$execWithLoading(async () => {
        const rsp = await getAppointment(this.data.id);
        this.detail = {
          ...rsp,
          startTime: this.$timestampToDateTimeMinute(rsp.start_at),
          length: ((rsp.end_at - rsp.start_at) / 3600).toFixed(2),
          canEdit: rsp.start_at >= todayTs,
          canCancel: nowTs < rsp.end_at + 86400,
          // 晤談日 2 天內可切換支付狀態
          canChangePaid: nowTs < rsp.end_at + 2 * 86400,
        };
      }, () => {
        this.$showError('讀取詳細記錄失敗');
      });
    },
    execCommand(cmd) {
      const appointment = this.detail;

      if (cmd === 'edit') {
        this.$emit('edit', appointment);
      } else if (cmd === 'send') {
        this.tryOpenChat(appointment.user.id);
      } else if (cmd === 'copyLink') {
        this.$copy(appointment.meeting_link, true);
      } else if (cmd === 'cancel') {
        this.$emit('cancel', appointment);
      } else if (cmd === 'downloadReceipt') {
        this.$goLink(appointment.receipt);
      } else if (cmd === 'editNote') {
        this.openNoteEdit(appointment);
      } else if (cmd === 'recreateAppointment') {
        this.$emit('recreate', {
          user: appointment.user,
          renew: true,
        });
      }
    },
    async openNoteEdit(appointment) {
      const result = await this.$prompt('請輸入備註', '備註編輯', {
        confirmButtonText: '儲存',
        cancelButtonText: '取消',
        inputValue: appointment.note,
        closeOnClickModal: false,
      });
      this.$execWithLoading(async () => {
        await updateAppointmentNote(appointment.id, result.value);
        // eslint-disable-next-line no-param-reassign
        appointment.note = result.value;
        this.$forceUpdate();
        this.$showSuccess('更新備註成功');
      }, (e) => {
        this.$showAxiosException('更新備註失敗', e);
      });
    },
    async tryOpenChat(userID) {
      return this.$execWithLoading(async () => {
        const data = await getUser(userID);
        if (data.line_chat_id && data.line_chat_id !== '') {
          this.$goLink(`${constant.lineChatURL}/${data.line_chat_id}`);
        } else {
          this.$showError('該使用者尚未設定 LINE 聊天 ID');
        }
      }, (e) => this.$showAxiosException('讀取個案資料失敗', e));
    },
  },
  mounted() {
    setTimeout(() => {
      this.loadData();
    }, 100);
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

.appointment-detail {
  .op {
    flex: 0 0 auto;
  }
  .data {
    .title {
      color: $text-secondary-color;
      font-size: 12px;
    }
  }
}
</style>
